// withDraftFeature.jsx
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { QRCodeSVG } from "qrcode.react";
import { HttpClient } from "../../Redux/controllers/HttpClient";
import Utility from "../../Utils/Utility";
import { Save } from "@mui/icons-material";
import { CiShare2 } from "react-icons/ci";

const withDraftFeature = (WrappedComponent, formType) => {
  return function WithDraftFeature(props) {
    // Get token from Redux
    const token = useSelector((state) => state?.user?.token);
    const userInfo = useSelector((state) => state?.user?.userInfo?.data?.user);

    const [draftId, setDraftId] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [filledFields, setFilledFields] = useState({});
    const [showShareModal, setShowShareModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();

    useEffect(() => {
      const params = new URLSearchParams(location.search);

      const accessToken = params.get("token");
      const pathParts = location.pathname.split("/");
      const draftIdFromPath = pathParts[pathParts.length - 1];
      console.log("accessToken", accessToken);

      if (draftIdFromPath && accessToken) {
        setAccessToken(accessToken);
        loadDraft(draftIdFromPath, accessToken);
      }
    }, [location]);

    const loadDraft = async (draftId, accessToken) => {
      try {
        const response = await HttpClient.apiCaller(
          `drafts/${draftId}?token=${accessToken}`,
          "GET",
          null,
          token || accessToken // Use token if logged in, otherwise use access token
        );

        if (response.data.success) {
          setDraftId(draftId);

          console.log("response", response.data);
          setFilledFields(response.data.data.formData || {});
          // Pass loaded data to form
          if (props.onDraftLoad) {
            props.onDraftLoad(response.data.formData);
          }
        }
      } catch (error) {
        Utility.eToast("Error loading draft");
      }
    };

    const saveDraft = async (formData) => {
      try {
        setIsLoading(true);

        const response = await HttpClient.apiCaller(
          draftId ? `drafts/${draftId}?token=${accessToken}` : "drafts",
          draftId ? "PUT" : "POST",
          {
            formData,
            formType,
            filledFields,
            createdBy: userInfo?._id,
          },
          token
        );

        if (response.data.success) {
          setDraftId(response.data.draftId);
          Utility.sToast("Draft saved successfully");
        }
      } catch (error) {
        Utility.eToast("Error saving draft");
      } finally {
        setIsLoading(false);
      }
    };

    const handleFieldChange = (field, value) => {
      // Allow changes if user is logged in or field is not filled
      if (!token && filledFields[field]) return;

      if (value && !filledFields[field]) {
        setFilledFields((prev) => ({
          ...prev,
          [field]: {
            filledAt: new Date(),
            filledBy: userInfo?._id || "anonymous",
          },
        }));
      }
    };

    console.log("filledFieldsDraft on with", filledFields);

    // Share Modal Component
    const ShareModal = () => {
      const shareableLink = `${window.location.origin}/form/${formType}/draft/${draftId}?token=${token}`;

      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Share Form</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-500"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Shareable Link</label>
                <div className="flex mt-1 gap-2">
                  <input
                    type="text"
                    value={shareableLink}
                    readOnly
                    className="flex-1 p-2 text-sm border rounded bg-gray-50"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(shareableLink);
                      Utility.sToast("Link copied to clipboard");
                    }}
                    className="p-2 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600">QR Code</label>
                <div className="mt-2 p-4 bg-white flex justify-center">
                  <QRCodeSVG value={shareableLink} size={200} />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <>
        <WrappedComponent
          {...props}
          token={token}
          userInfo={userInfo}
          draftId={draftId}
          filledFields={filledFields}
          onFieldChange={handleFieldChange}
          onSaveDraft={saveDraft}
          isLoading={isLoading}
        />
        {showShareModal && <ShareModal />}
      </>
    );
  };
};

export default withDraftFeature;
